import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { getBanks } from '@/services/Bank/request';
import { Bank } from '@/services/Bank/types';
import { getPartnersFinancialWithdrawRequest } from '@/services/Partner/Financial';
import { getPartnersBankDetails } from '@/services/Partner/Financial/BankDetails/request';
import { GetPartnersBankDetailsBase } from '@/services/Partner/Financial/BankDetails/types';
import { IPartinersFinancialWithdrawBase } from '@/services/Partner/Financial/Withdraw/Request/types';

import { IFilters } from '@/pages/Partners/types/apis/indications';

export type RequiredBank = Required<Bank>;

interface IndicationSummaryData {
  total: number;
  ganhos: number;
  andamento: number;
  perdas: number;
}

export type IndicationCounts = {
  locacao: IndicationSummaryData;
  spot: IndicationSummaryData;
  b2b: IndicationSummaryData;
  terreno: IndicationSummaryData;
  marketplace: IndicationSummaryData;
  parceiro?: IndicationSummaryData;
  franchise?: IndicationSummaryData;
  decoracao?: IndicationSummaryData;
};

interface ILoading {
  aux: boolean;
  main: boolean;
  indications: boolean;
  message: string | undefined;
}

interface IHideElements {
  footer?: { is: boolean; animate?: boolean };
  header?: { is: boolean; animate?: boolean };
  whatsapp?: { is: boolean; animate?: boolean };
}

export interface PartnersState {
  banks: RequiredBank[];
  loadingBanks: boolean;
  financialWithdrawRequest: IPartinersFinancialWithdrawBase[];
  loadingFinancialRequest: boolean;
  bankDetails: GetPartnersBankDetailsBase[];
  loadingBankDetails: boolean;
  filters: IFilters;
  successScreen: boolean;
  /**
   * `spotFlowStep` saiu daqui.
   *
   * Ele guardava a etapa do formulário de SPOT (`step`), a indicação
   * (`indicationId`) e o par empreendimento/unidade que a etapa 2 usava para
   * pedir condições de venda e fluxo padrão. Estado de Redux não sobrevive a um
   * F5: recarregar a página no meio da etapa 2 devolvia o formulário VAZIO na
   * etapa 1, e reenviar dali criava uma indicação duplicada da mesma unidade.
   * Ao mesmo tempo ele sobrevivia demais — saía da tela intacto, e a indicação
   * SEGUINTE abria na etapa 2 da anterior, o que obrigava o `Content` a limpá-lo
   * na montagem e a distinguir retomada legítima de herança.
   *
   * Hoje a etapa é o ENDEREÇO (`indicar/formulario/spot/nova` e `.../:id`) e os
   * dois ids saem do `journey/` da indicação — que, ao contrário do docblock que
   * ficou aqui por muito tempo, os devolve. Ver `Content.tsx` e
   * `SpotSteps/Two/Two.tsx`.
   */
  indicationCounts: IndicationCounts;
  loading: ILoading;
  isIndicationsPage: boolean;
  welcome: { is: boolean };
  hideElements: IHideElements;
}

const initialFilters: IFilters = {
  page: 1,
  page_size: 10,
  activeStatus: undefined,
  activePage: 'locacao',
};

const initialState: PartnersState = {
  banks: [],
  loadingBanks: false,
  financialWithdrawRequest: [],
  loadingFinancialRequest: false,
  bankDetails: [],
  loadingBankDetails: false,
  filters: initialFilters,
  successScreen: false,
  indicationCounts: {
    locacao: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    spot: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    b2b: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    terreno: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    marketplace: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    parceiro: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    franchise: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
    decoracao: { total: 0, ganhos: 0, andamento: 0, perdas: 0 },
  },
  loading: {
    main: false,
    aux: false,
    indications: false,
    message: undefined,
  },
  isIndicationsPage: false,
  welcome: { is: true },
  hideElements: {
    footer: { is: false, animate: false },
    header: { is: false, animate: false },
    whatsapp: { is: false, animate: false },
  },
};

export const fetchBanks = createAsyncThunk(
  'partners/fetchBanks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getBanks();
      return response as RequiredBank[];
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Erro ao buscar bancos');
    }
  },
);

export const fetchFinancialWithdrawRequest = createAsyncThunk(
  'partners/fetchFinancialWithdrawRequest',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPartnersFinancialWithdrawRequest();
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Erro ao buscar solicitações de saque');
    }
  },
);

export const fetchPartnersBankDetails = createAsyncThunk(
  'partners/fetchPartnersBankDetails',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await getPartnersBankDetails({ user_id: userId });
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Erro ao buscar dados bancários');
    }
  },
);

const partnersSlice = createSlice({
  name: 'partners',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<IFilters>) {
      state.filters = action.payload;
    },
    setSuccessScreen(state, action: PayloadAction<boolean>) {
      state.successScreen = action.payload;
    },
    setIndicationCounts(state, action: PayloadAction<IndicationCounts>) {
      state.indicationCounts = action.payload;
    },
    setLoading(state, action: PayloadAction<ILoading>) {
      state.loading = action.payload;
    },
    setIsIndicationsPage(state, action: PayloadAction<boolean>) {
      state.isIndicationsPage = action.payload;
    },
    setWelcome(state, action: PayloadAction<{ is: boolean }>) {
      state.welcome = action.payload;
    },
    setHideElements(state, action: PayloadAction<IHideElements>) {
      state.hideElements = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Banks
    builder.addCase(fetchBanks.pending, (state) => {
      state.loadingBanks = true;
    });
    builder.addCase(fetchBanks.fulfilled, (state, action) => {
      state.banks = action.payload;
      state.loadingBanks = false;
    });
    builder.addCase(fetchBanks.rejected, (state) => {
      state.banks = [];
      state.loadingBanks = false;
    });

    // Financial Withdraw Request
    builder.addCase(fetchFinancialWithdrawRequest.pending, (state) => {
      state.loadingFinancialRequest = true;
    });
    builder.addCase(fetchFinancialWithdrawRequest.fulfilled, (state, action) => {
      state.financialWithdrawRequest = action.payload;
      state.loadingFinancialRequest = false;
    });
    builder.addCase(fetchFinancialWithdrawRequest.rejected, (state) => {
      state.financialWithdrawRequest = [];
      state.loadingFinancialRequest = false;
    });

    // Bank Details
    builder.addCase(fetchPartnersBankDetails.pending, (state) => {
      state.loadingBankDetails = true;
    });
    builder.addCase(fetchPartnersBankDetails.fulfilled, (state, action) => {
      state.bankDetails = action.payload;
      state.loadingBankDetails = false;
    });
    builder.addCase(fetchPartnersBankDetails.rejected, (state) => {
      state.bankDetails = [];
      state.loadingBankDetails = false;
    });
  },
});

export const {
  setFilters,
  setSuccessScreen,
  setIndicationCounts,
  setLoading,
  setIsIndicationsPage,
  setWelcome,
  setHideElements,
} = partnersSlice.actions;

export default partnersSlice.reducer;
